import React, { useEffect, useRef, useState } from 'react';
import { fetchRegionalResumo, RegionalResumoItemDto } from '../../../../api/contac/contacAnalyticsApi';
import ArrowDown from '../../../../img/arrow-down.svg';
import ArrowUp from '../../../../img/arrow-up.svg';
import { toast } from 'react-toastify';
import '../placa-dre/ModalPlacaDre.css';

interface ModalRegionalDreProps {
  codigoRegional: number;
  nomeRegional: string;
  dataInicio: string;
  dataFim: string;
  isOpen: boolean;
  onClose: () => void;
  baseTag?: string;
}

type Detalhe = { mes: string; valor: number };
type Classificacao = { classificacao: string; total: number; detalhes: Detalhe[] };

const classBySign3 = (n?: number) =>
  typeof n === 'number' && n < 0 ? 'value-negative3' : 'value-positive3';

const formatCurrency = (valor: number | null | undefined) =>
  typeof valor === 'number'
    ? valor.toLocaleString('pt-BR', { minimumFractionDigits: 2 })
    : '0,00';

const formatPercent = (n: number | null | undefined) =>
  typeof n === 'number' && Number.isFinite(n)
    ? `${n.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`
    : '—';

const pctOf = (signedValue: number, baseAbs: number) =>
  baseAbs === 0 ? null : (signedValue / baseAbs) * 100;

const renderValorPct = (
  displayValue: React.ReactNode,
  signedForPercent: number,
  baseAbs: number,
  percentClassName?: string
) => (
  <div className="dre-value-pct">
    {displayValue}
    <span className={`dre-percent ${percentClassName ?? ''}`}>
      {formatPercent(pctOf(signedForPercent, baseAbs))}
    </span>
  </div>
);

const toNum = (v: any) => (typeof v === 'number' ? v : Number(v ?? 0)) || 0;

const buildClassificacoesRegional = (
  competencias: Record<string, RegionalResumoItemDto[]> | undefined,
  tipo: 'ENTRADA' | 'SAIDA',
) => {
  const acc: Record<string, { classificacao: string; total: number; porMes: Record<string, number> }> = {};
  for (const [mes, linhas] of Object.entries(competencias ?? {})) {
    if (!/^\d{2}\/\d{4}$/.test(mes)) continue;
    for (const l of linhas ?? []) {
      if (l?.linha !== 'CLASSIFICACAO') continue;
      const key = (l?.classificacao ?? '').trim() || 'Sem Classificação';
      const v = tipo === 'ENTRADA' ? toNum(l.total_entradas) : toNum(l.total_saidas);
      if (v === 0) continue;
      if (!acc[key]) acc[key] = { classificacao: key, total: 0, porMes: {} };
      acc[key].total += v;
      acc[key].porMes[mes] = (acc[key].porMes[mes] ?? 0) + v;
    }
  }
  return Object.values(acc)
    .map((x) => ({
      classificacao: x.classificacao,
      total: x.total,
      detalhes: Object.entries(x.porMes).map(([competencia, valor]) => ({ mes: competencia, valor })),
    }))
    .sort((a, b) => b.total - a.total);
};

const ModalRegionalDre: React.FC<ModalRegionalDreProps> = ({
  codigoRegional,
  nomeRegional,
  dataInicio,
  dataFim,
  isOpen,
  onClose,
  baseTag,
}) => {
  const modalRef = useRef<HTMLDivElement>(null);

  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState<string[]>([]);
  const [resultado, setResultado] = useState<any | null>(null);

  const toggleExpand = (key: string) =>
    setExpanded((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));

  useEffect(() => {
    if (!isOpen || !codigoRegional) return;

    let active = true;

    const run = async () => {
      try {
        setLoading(true);

        const resp = await fetchRegionalResumo({
          dataInicio,
          dataFim,
          codigo_regional: codigoRegional,
          base_tag: baseTag,
        });

        if (!active) return;

        const d = resp?.detalhamento || {};
        const competencias = d as Record<string, any>;

        const totalReceita = toNum(d.totaisEntrada);
        const totalSaida = toNum(d.totaisSaida);
        const resultadoFinal = toNum(d.resultado);

        const entradasOp = buildClassificacoesRegional(competencias, 'ENTRADA');
        const saidasOp = buildClassificacoesRegional(competencias, 'SAIDA');

        setResultado({
          regional: resp.regional || nomeRegional,
          codigoRegional: String(codigoRegional),
          totaisEntrada: totalReceita,
          totaisSaida: totalSaida,
          resultadoFinal,
          placasAtivas: toNum(resp.placasAtivas),
          placasPeriodo: toNum(resp.placasPeriodo),
          entradasClassificacoes: entradasOp,
          saidasClassificacoes: saidasOp,
        });

        setExpanded([]);
      } catch (err: any) {
        if (!active) return;
        toast.error(err.message || 'Erro ao buscar DRE da regional.');
        setResultado(null);
      } finally {
        if (!active) return;
        setLoading(false);
      }
    };

    run();
    return () => {
      active = false;
    };
  }, [codigoRegional, dataInicio, dataFim, isOpen, nomeRegional, baseTag]);

  useEffect(() => {
    if (!isOpen) return;

    const handlePointerDown = (event: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(event.target as Node)) onClose();
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const baseRbAbs = Math.abs(Number(resultado?.totaisEntrada) || 0);

  return (
      <div className="status-modal-overlay">
        <div className="status-modal-dre-content" ref={modalRef} style={{ margin: '0 auto' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
            <div className="modal-header">{resultado?.regional || nomeRegional}</div>
          </div>

          <div className="modal-body" style={loading ? { flexDirection: 'column', justifyContent: 'center', alignItems: 'center', padding: '60px 0', gap: '10px' } : undefined}>
            {loading ? (
              <>
                <div className="dre-spinner" />
                <span style={{ fontSize: '14px', color: 'var(--tema-texto-2, #666)', fontWeight: '500', fontFamily: "'AmpleSoft-Regular', sans-serif" }}>Carregando...</span>
              </>
            ) : !resultado ? (
              <div className="card">
                <div className="card-entry">Nenhum dado encontrado para esta regional nesse período.</div>
              </div>
            ) : (
              <>
                {/* Card 1 - Informações */}
                <div className="card card-left">
                  <div className="modal-header font-family-outfit">Suas informações</div>
                  <div className="info-row">
                    <span className="info-label">Regional</span>
                    <span className="info-value">{resultado.regional}</span>
                  </div>
                  <div className="info-row">
                    <span className="info-label">Código</span>
                    <span className="info-value">{resultado.codigoRegional}</span>
                  </div>
                  <div className="info-row">
                    <span className="info-label">Período</span>
                    <span className="info-value">{`${dataInicio.replace(/(\d{4})-(\d{2})-(\d{2})/g, '$3-$2-$1')} a ${dataFim.replace(/(\d{4})-(\d{2})-(\d{2})/g, '$3-$2-$1')}`}</span>
                  </div>
                </div>

                {/* Card 2 - DRE */}
                <div className="card">
                  <div className="modal-header font-family-outfit">Demonstrativo de Resultados</div>

                  <div className="row-between dre-columns-header" style={{ marginBottom: 12, borderBottom: '1px dashed var(--tema-linha, #eef2f5)', paddingBottom: 6 }}>
                    <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
                      <span className="dre-col-header" style={{ fontSize: '11px', display: 'flex', alignItems: 'center' }}>
                        Placas Ativas: <strong style={{ color: 'var(--tema-texto, #1A1C1B)', marginLeft: 4 }}>{Number(resultado?.placasAtivas ?? 0)}</strong>
                      </span>
                      <span className="dre-col-header" style={{ fontSize: '11px', display: 'flex', alignItems: 'center' }}>
                        Placas do Período: <strong style={{ color: 'var(--tema-texto, #1A1C1B)', marginLeft: 4 }}>{Number(resultado?.placasPeriodo ?? 0)}</strong>
                      </span>
                    </div>
                    <div className="dre-value-pct">
                      <span className="dre-col-header" style={{ minWidth: 120, textAlign: 'right' }}>Valor</span>
                      <span className="dre-col-header" style={{ minWidth: 70, textAlign: 'right' }}>% Rec. Bruta</span>
                    </div>
                  </div>

                  {/* Receita Total (Entradas) */}
                  <div
                    className="row-between clickable"
                    onClick={() => toggleExpand('ent')}
                    aria-expanded={expanded.includes('ent')}
                  >
                    <div className="dre-title">
                      Receita Total <span className="dre-note">(Entradas)</span>
                      <img
                        style={{ marginLeft: 5 }}
                        src={expanded.includes('ent') ? ArrowUp : ArrowDown}
                        alt="Toggle"
                        className="imgIcon arrow-toggle"
                      />
                    </div>
                    {renderValorPct(
                      <span className="entry-text value-positive3">R$ {formatCurrency(resultado?.totaisEntrada)}</span>,
                      +Math.abs(Number(resultado?.totaisEntrada) || 0),
                      baseRbAbs,
                      'value-positive5'
                    )}
                  </div>

                  {expanded.includes('ent') && (
                    <div className="subclass-container">
                      {(resultado?.entradasClassificacoes || []).map((ent: Classificacao, idx: number) => (
                        <React.Fragment key={`ent-op-${idx}`}>
                          <div className="subclass-row">
                            <div className="subclass-label">
                              <span>{ent.classificacao}</span>
                              <img
                                src={expanded.includes(`ent-op-${idx}`) ? ArrowUp : ArrowDown}
                                alt="Toggle"
                                className="imgIcon arrow-toggle clickable"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toggleExpand(`ent-op-${idx}`);
                                }}
                              />
                            </div>
                            <div className="subclass-value-pct">
                              <span className="subclass-value value-positive4">R$ {formatCurrency(ent.total)}</span>
                              <span className="dre-percent" style={{ visibility: 'hidden' }}>—</span>
                            </div>
                          </div>

                          {expanded.includes(`ent-op-${idx}`) &&
                            (ent.detalhes || []).map((det, dIdx) => (
                              <div className="detail-row" key={`ent-op-det-${idx}-${dIdx}`}>
                                <span className="detail-label">{det.mes}</span>
                                <div className="subclass-value-pct">
                                  <span className="detail-value value-positive4">R$ {formatCurrency(det.valor)}</span>
                                  <span className="dre-percent" style={{ visibility: 'hidden' }}>—</span>
                                </div>
                              </div>
                            ))}
                        </React.Fragment>
                      ))}
                    </div>
                  )}

                  {/* Despesa Total (Saídas) */}
                  <div
                    className="row-between clickable"
                    onClick={() => toggleExpand('sai')}
                    aria-expanded={expanded.includes('sai')}
                  >
                    <div className="dre-title">
                      Despesa Total <span className="dre-note">(Saídas)</span>
                      <img
                        style={{ marginLeft: 5 }}
                        src={expanded.includes('sai') ? ArrowUp : ArrowDown}
                        alt="Toggle"
                        className="imgIcon arrow-toggle"
                      />
                    </div>
                    {renderValorPct(
                      <span className="entry-text value-negative3">
                        R$ -{formatCurrency(resultado?.totaisSaida)}
                      </span>,
                      -Math.abs(Number(resultado?.totaisSaida) || 0),
                      baseRbAbs,
                      'value-negative5'
                    )}
                  </div>

                  {expanded.includes('sai') && (
                    <div className="subclass-container">
                      {(resultado?.saidasClassificacoes || []).map((sai: Classificacao, idx: number) => (
                        <React.Fragment key={`sai-dir-${idx}`}>
                          <div className="subclass-row">
                            <div className="subclass-label">
                              <span>{sai.classificacao}</span>
                              <img
                                src={expanded.includes(`sai-dir-${idx}`) ? ArrowUp : ArrowDown}
                                alt="Toggle"
                                className="imgIcon arrow-toggle clickable"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toggleExpand(`sai-dir-${idx}`);
                                }}
                              />
                            </div>
                            <div className="subclass-value-pct">
                              <span className="subclass-value value-negative4">R$ {formatCurrency(sai.total)}</span>
                              <span className="dre-percent" style={{ visibility: 'hidden' }}>—</span>
                            </div>
                          </div>

                          {expanded.includes(`sai-dir-${idx}`) &&
                            (sai.detalhes || []).map((det, dIdx) => (
                              <div className="detail-row" key={`sai-dir-det-${idx}-${dIdx}`}>
                                <span className="detail-label">{det.mes}</span>
                                <div className="subclass-value-pct">
                                  <span className="detail-value value-negative4">R$ {formatCurrency(det.valor)}</span>
                                  <span className="dre-percent" style={{ visibility: 'hidden' }}>—</span>
                                </div>
                              </div>
                            ))}
                        </React.Fragment>
                      ))}
                    </div>
                  )}

                  {/* Resultado Final */}
                  <div className="row-between" style={{ borderTop: '2px solid #EA5D0E', paddingTop: '20px', marginTop: '16px' }}>
                    <div className="dre-title font-family-outfit" style={{ fontSize: '16px', fontWeight: 'bold' }}>
                      Resultado <span className="dre-note">(Receita - Despesa)</span>
                    </div>
                    {renderValorPct(
                      <span className={`entry-text font-family-outfit ${classBySign3(resultado?.resultadoFinal)}`} style={{ fontSize: '20px', fontWeight: 'bold' }}>
                        R$ {formatCurrency(resultado?.resultadoFinal)}
                      </span>,
                      Number(resultado?.resultadoFinal) || 0,
                      baseRbAbs,
                      (Number(resultado?.resultadoFinal) || 0) < 0 ? 'value-negative5' : 'value-positive5'
                    )}
                  </div>

                  <div className="modal-footer">
                    <button className="modal-close-btn font-family-outfit" onClick={onClose}>
                      Fechar
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
  );
};

export default ModalRegionalDre;
